import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, Linkedin, Instagram, ArrowUpRight } from "lucide-react";
import { analytics } from "../firebase";
import { logEvent } from "firebase/analytics";

const About = () => {
  const leftMirrorRef = useRef(null);
  const rightMirrorRef = useRef(null);
  const mainVideoContainerRef = useRef(null);
  const youtubePlayerRef = useRef(null);
  const [primarySize, setPrimarySize] = useState({ width: 0, height: 0 });
  const [preferPosters, setPreferPosters] = useState(false);
  const sectionRef = useRef(null);
  const [parallax, setParallax] = useState(0);
  const [youtubeReady, setYoutubeReady] = useState(false);

  // Keep mirror containers exactly the same size as the main video container
  useEffect(() => {
    const element = mainVideoContainerRef.current;
    if (!element) return;

    const updateSize = () => {
      const rect = element.getBoundingClientRect();
      setPrimarySize({ width: rect.width, height: rect.height });
    };

    updateSize();
    let resizeObserver;
    try {
      resizeObserver = new ResizeObserver(updateSize);
      resizeObserver.observe(element);
    } catch (_) {
      // Fallback if ResizeObserver is not available
      window.addEventListener("resize", updateSize);
    }

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", updateSize);
    };
  }, []);

  // Load YouTube API
  useEffect(() => {
    if (window.YT && window.YT.Player) {
      setYoutubeReady(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    script.async = true;
    document.head.appendChild(script);

    window.onYouTubeIframeAPIReady = () => {
      setYoutubeReady(true);
    };

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // Prefer poster images on slow connections or with data saver
  useEffect(() => {
    try {
      const conn = navigator?.connection;
      if (conn) {
        const slow = ["slow-2g", "2g"].includes(conn.effectiveType || "");
        const saver = Boolean(conn.saveData);
        if (slow || saver) setPreferPosters(true);
      }
    } catch (_) {
      // no-op
    }
  }, []);

  // Lightweight parallax for mirrors
  useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewport = window.innerHeight || 1;
      const progress = Math.min(
        1,
        Math.max(0, (viewport - rect.top) / (viewport + rect.height))
      );
      setParallax((progress - 0.5) * 30);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // GA4 sending helper (falls back to window.gtag if Firebase Analytics isn't available)
  const sendAnalyticsEvent = (eventName, params = {}) => {
    const isDebug =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).has("debug_mode");
    const eventParams = { ...params };
    if (isDebug) eventParams.debug_mode = true;

    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", eventName, eventParams);
      return;
    }
    if (analytics) {
      logEvent(analytics, eventName, eventParams);
    }
  };

  // GA4 Video tracking functions
  const trackVideoEvent = (eventName, progress = null) => {
    const player = youtubePlayerRef.current;
    const eventData = {
      video_title: "VSL September 2025",
      video_url: "https://www.youtube.com/watch?v=v5eNSrDKTDo",
      video_current_time: player?.getCurrentTime() || 0,
      video_duration: player?.getDuration() || 0,
    };
    if (progress !== null) {
      eventData.video_percent = progress;
    }
    sendAnalyticsEvent(eventName, eventData);
  };

  // Sync mirror videos with main video (now just for tracking)
  const syncMirrorVideos = (action, currentTime = null) => {
    // Mirrors are now static images, so we just track the action
    console.log(`Mirror sync: ${action} at ${currentTime}s`);
  };

  // YouTube player event handlers
  const onPlayerReady = (event) => {
    youtubePlayerRef.current = event.target;
  };

  const onPlayerStateChange = (event) => {
    const player = youtubePlayerRef.current;
    if (!player) return;

    switch (event.data) {
      case window.YT.PlayerState.PLAYING:
        trackVideoEvent("video_start");
        syncMirrorVideos("play", player.getCurrentTime());
        break;
      case window.YT.PlayerState.PAUSED:
        trackVideoEvent("video_pause");
        syncMirrorVideos("pause");
        break;
      case window.YT.PlayerState.ENDED:
        trackVideoEvent("video_complete", 100);
        syncMirrorVideos("pause");
        break;
    }
  };

  const onPlayerTimeUpdate = () => {
    const player = youtubePlayerRef.current;
    if (!player) return;

    const currentTime = player.getCurrentTime();
    const duration = player.getDuration();
    const progress = Math.round((currentTime / duration) * 100);

    // Track quarter milestones
    if (progress === 25 || progress === 50 || progress === 75) {
      trackVideoEvent("video_progress", progress);
    }
  };

  // Initialize YouTube player
  useEffect(() => {
    if (!youtubeReady || youtubePlayerRef.current) return;

    const player = new window.YT.Player("youtube-player", {
      host: "https://www.youtube-nocookie.com",
      videoId: "v5eNSrDKTDo",
      width: "100%",
      height: "100%",
      playerVars: {
        origin: window.location.origin,
        controls: 1,
        modestbranding: 1,
        rel: 0,
        showinfo: 0,
        iv_load_policy: 3,
        fs: 1,
        cc_load_policy: 0,
        playsinline: 1,
        autoplay: 0,
        mute: 0,
      },
      events: {
        onReady: onPlayerReady,
        onStateChange: onPlayerStateChange,
      },
    });

    youtubePlayerRef.current = player;

    return () => {
      if (player && player.destroy) {
        player.destroy();
      }
    };
  }, [youtubeReady]);

  // Set up time update interval for YouTube player
  useEffect(() => {
    if (!youtubeReady || !youtubePlayerRef.current) return;

    const interval = setInterval(() => {
      if (
        youtubePlayerRef.current &&
        youtubePlayerRef.current.getPlayerState() ===
          window.YT.PlayerState.PLAYING
      ) {
        onPlayerTimeUpdate();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [youtubeReady]);

  const handleContactClick = () => {
    // Track CTA click
    sendAnalyticsEvent("video_cta_click", {
      cta_location: "about_video",
      cta_text: "Book Your Call",
    });

    // Scroll to contact section
    const contactSection = document.getElementById("contact");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });

      // Wait for scroll to finish, then trigger the contact form
      setTimeout(() => {
        const bookCallButton = contactSection.querySelector(
          'button[data-book-call="true"]'
        );
        if (bookCallButton) {
          bookCallButton.click();
        }
      }, 1000);
    }
  };

  const teamMembers = [
    {
      name: "Luke Moseley",
      role: "Co-Founder",
      image: "/images/Luke_green.webp",
      email: "luke@hearmeoutmusic.co",
      linkedin: "https://www.linkedin.com/in/luke-moseley-b51aa647/",
      instagram: "https://www.instagram.com/moseleynoise",
    },
    {
      name: "George Holliday",
      role: "Co-Founder",
      image: "/images/George_green.webp",
      email: "george@hearmeoutmusic.co",
      linkedin: "https://www.linkedin.com/in/georgeholliday/",
      instagram: "https://www.instagram.com/georgejazzmanholliday/",
    },
  ];

  // Container animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  // Individual element animation variants
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.25, 0, 0.95],
      },
    },
  };

  // Text fade in variants
  const textVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut",
      },
    },
  };

  return (
    <section className="relative py-24 bg-[#111111]">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Animated Background Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 1.2 }}
          animate={{ opacity: 0.3 }}
          style={{
            rotate: [0, 3, -3, 0],
          }}
          transition={{
            opacity: { duration: 2 },
            rotate: {
              repeat: Infinity,
              duration: 20,
              ease: "easeInOut",
            },
          }}
          className="absolute inset-0"
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <img
              src="/HEAR ME OUT - Logo - Purple.svg"
              alt=""
              className="w-[200%] md:w-[150%] max-w-none opacity-30 -mt-[25%]"
              style={{ filter: "blur(2px)" }}
            />
          </div>
        </motion.div>

        {/* Dot Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,_rgb(1,178,128)_1px,_transparent_0)] [background-size:40px_40px] opacity-5" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header Text */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center max-w-4xl mx-auto mb-16"
        >
          <motion.div variants={textVariants}>
            <h1 className="text-4xl md:text-6xl font-light text-white/90 mb-8">
              Meet The Hear Me Out
              <br />
              <span className="font-bold">Sonic Branding Team</span>
            </h1>
          </motion.div>

          {/* Video Section with Mirror Effect */}
          <motion.div
            variants={textVariants}
            className="w-full max-w-none mx-auto"
          >
            {/* Full-width container with mirror effect */}
            <div ref={sectionRef} className="relative w-full mb-8">
              {/* Three-column layout: left mirror, main video, right mirror */}
              <div className="flex items-stretch justify-center w-full gap-0">
                {/* Left Mirror Reflection */}
                <div
                  className="relative overflow-hidden flex-none z-0 pointer-events-none hidden xl:block"
                  style={{
                    width: primarySize.width,
                    height: primarySize.height,
                  }}
                >
                  <div
                    className="absolute inset-0 flex items-center justify-end -mr-[3.75rem]"
                    style={{
                      WebkitMaskImage:
                        "linear-gradient(to right, transparent 0%, black 25%, black 100%)",
                      maskImage:
                        "linear-gradient(to right, transparent 0%, black 25%, black 100%)",
                    }}
                  >
                    <div
                      className="w-full h-full"
                      style={{
                        WebkitMaskImage:
                          "linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)",
                        maskImage:
                          "linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)",
                      }}
                    >
                      <img
                        src="https://img.youtube.com/vi/v5eNSrDKTDo/maxresdefault.jpg"
                        alt="Mirror left poster"
                        className="block w-full h-full object-cover"
                        style={{
                          transform: "scaleX(-1)",
                          filter:
                            "blur(10px) brightness(0.25) contrast(0.65) saturate(0.8)",
                        }}
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>

                {/* Main Video Container */}
                <div className="relative z-50 w-full max-w-2xl flex-none">
                  {/* Ambient glow behind */}
                  <div className="pointer-events-none absolute -inset-x-16 -inset-y-12 -z-10 bg-[radial-gradient(ellipse_at_center,rgba(111,87,165,0.25),rgba(1,178,128,0.18)_45%,transparent_70%)] blur-2xl" />

                  {/* Gradient frame */}
                  <div className="relative p-[2px] rounded-2xl overflow-hidden bg-gradient-to-r from-[#6F57A5]/60 to-[#01B280]/60 shadow-[0_40px_120px_rgba(0,0,0,0.45)]">
                    <div
                      ref={mainVideoContainerRef}
                      className="aspect-video w-full overflow-hidden bg-transparent"
                      style={{ borderRadius: "inherit" }}
                    >
                      {preferPosters ? (
                        <img
                          src="https://img.youtube.com/vi/v5eNSrDKTDo/maxresdefault.jpg"
                          alt="Video poster"
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : youtubeReady ? (
                        <div
                          id="youtube-player"
                          className="w-full h-full"
                          style={{ borderRadius: "inherit" }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-900 flex items-center justify-center rounded-2xl">
                          <div className="text-white text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                            <p>Loading video...</p>
                          </div>
                        </div>
                      )}

                      {/* Vignette overlay */}
                      <div
                        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_65%,rgba(0,0,0,0.45))]"
                        style={{ borderRadius: "inherit" }}
                      />
                    </div>
                  </div>
                </div>

                {/* Right Mirror Reflection */}
                <div
                  className="relative overflow-hidden flex-none z-0 pointer-events-none hidden xl:block"
                  style={{
                    width: primarySize.width,
                    height: primarySize.height,
                  }}
                >
                  <div
                    className="absolute inset-0 flex items-center justify-start -ml-[3.75rem]"
                    style={{
                      WebkitMaskImage:
                        "linear-gradient(to left, transparent 0%, black 25%, black 100%)",
                      maskImage:
                        "linear-gradient(to left, transparent 0%, black 25%, black 100%)",
                    }}
                  >
                    <div
                      className="w-full h-full"
                      style={{
                        WebkitMaskImage:
                          "linear-gradient(to bottom, transparent 0%, black 14%, black 86%, transparent 100%)",
                        maskImage:
                          "linear-gradient(to bottom, transparent 0%, black 14%, black 86%, transparent 100%)",
                      }}
                    >
                      <img
                        src="https://img.youtube.com/vi/v5eNSrDKTDo/maxresdefault.jpg"
                        alt="Mirror right poster"
                        className="block w-full h-full object-cover"
                        style={{
                          transform: "scaleX(-1)",
                          filter:
                            "blur(8px) brightness(0.5) contrast(0.8) saturate(0.9)",
                        }}
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="max-w-4xl mx-auto px-4">
              <motion.div variants={textVariants} className="text-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleContactClick}
                  className="px-8 py-4 bg-primary-green text-white rounded-full font-medium text-lg 
                             hover:bg-primary-green/90 transition-all inline-flex items-center justify-center gap-2
                             shadow-lg hover:shadow-primary-green/25"
                >
                  Book Your Call
                  <ArrowUpRight className="w-5 h-5" />
                </motion.button>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>

        {/* Team Members */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-12"
        >
          {teamMembers.map((member) => (
            <motion.div
              key={member.name}
              variants={itemVariants}
              className="relative bg-[#111111]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8 text-center"
              style={{
                background: `linear-gradient(135deg, rgba(111,87,165,0.05), transparent 50%, rgba(1,178,128,0.05))`,
              }}
            >
              <div className="absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_1px_1px,_rgb(1,178,128)_1px,_transparent_0)] [background-size:40px_40px]" />

              <div className="relative z-10">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                  className="mb-6"
                >
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-b from-primary-purple/30 via-transparent to-primary-green/5 rounded-full" />
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-64 h-64 mx-auto rounded-full relative z-10"
                    />
                  </div>
                </motion.div>
                <h3 className="text-2xl font-bold text-white/90 mb-2">
                  {member.name}
                </h3>
                <p className="text-primary-green text-lg mb-6">{member.role}</p>

                {/* Contact Information */}
                <div className="space-y-4 text-center">
                  <a href={`mailto:${member.email}`} className="block group">
                    <p className="text-white/50 mb-1 text-sm">Email</p>
                    <div className="flex items-center gap-2 text-white group-hover:text-primary-green transition-colors justify-center">
                      <Mail className="w-5 h-5" />
                      <span>{member.email}</span>
                    </div>
                  </a>
                  <div className="flex gap-4 justify-center">
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-[#111111]/80 border border-white/10 rounded-xl hover:border-primary-green/50 transition-colors group"
                    >
                      <Linkedin className="w-5 h-5 text-white group-hover:text-primary-green transition-colors" />
                    </a>
                    <a
                      href={member.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-[#111111]/80 border border-white/10 rounded-xl hover:border-primary-green/50 transition-colors group"
                    >
                      <Instagram className="w-5 h-5 text-white group-hover:text-primary-green transition-colors" />
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Background Gradient - Moved to bottom */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#111111] via-transparent to-[#111111] opacity-60 pointer-events-none" />
    </section>
  );
};

export default About;
